<template>
  <q-card flat bordered :dark="isDarkModeGlobal">
    <q-card-section class="row justify-between">
      <h6 class="text-primary">Limits</h6>
      <div class="q-gutter-md">
        <q-btn
          color="primary"
          icon="download"
          round
          @click="saveChartAsImage"
          class="q-pa-sm"
        >
          <q-tooltip>
            Save Chart as Image
          </q-tooltip>
        </q-btn>
        <q-btn
          icon="help"
          color="primary"
          round
          class="q-pa-sm"
        >
          <q-tooltip>
            Show note
          </q-tooltip>
          <q-popup-proxy :offset="[-70, 20]">
            <q-banner :dark="isDarkModeGlobal">
              <template #avatar>
                <q-icon
                  name="help"
                  color="primary"
                />
              </template>
              <div v-html="graphDescription" />
            </q-banner>
          </q-popup-proxy>
        </q-btn>
      </div>
    </q-card-section>
    <q-card-section class="row flex justify-center q-gutter-md">
      <v-chart
        ref="chartRef"
        id="chartRef"
        class="chart"
        style="width: 600px; max-width: 800px; height: 400px;"
        autoresize
        :init-options="initOptions"
        :manual-update="true"
      />
    </q-card-section>
  </q-card>
</template>
<script setup lang="ts">
import { storeToRefs } from "pinia"
import { useCalculatorStore } from '../stores/calculator.store'
const calculatorStore = useCalculatorStore()

const {
  ppp_mpa,
  derated_ppp_mpa,
  isDarkModeGlobal,
  coupling_thickness_mm,
  focal_depth_mm,
  frequency_MHz
} = storeToRefs(calculatorStore)

import * as echarts from 'echarts/core'
import { SVGRenderer } from 'echarts/renderers'
import {
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  GraphicComponent
} from 'echarts/components'
import { LineChart } from 'echarts/charts'
import { UniversalTransition } from 'echarts/features'

import VChart from 'vue-echarts'
import {onMounted, ref, watch} from 'vue'
import type ECharts from "vue-echarts";
import {EChartsInitOpts} from "echarts/core";

echarts.use([
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  LineChart,
  SVGRenderer,
  UniversalTransition,
  GraphicComponent
])

// Reference to the chart component
const chartRef = ref<InstanceType<typeof ECharts> | null>(null)
const graphDescription = `This graph depicts the pressure corresponding to the MI limit. ` +
    `The dark blue line corresponds to the <a href="https://doi.org/10.1016/j.brs.2025.10.007" target="_blank"><strong>ITRUSST consensus</strong></a> ` +
    `on non-significant risk (MI &lt;= 1.9) based on the formal definition i.e., MI estimated using the peak-rarefactional pressure in MPa, ` +
    `derated using an attenuation coefficient of 0.3 dB/cm/MHz. The derating is applied from the coupling distance to the focal distance. ` +
    `Therefore, the derated pressure at the MI limit changes with depth. ` +
    `Please note that the line is not based on the input pressure provided in the input parameters panel but instead shows the pressure limit ` +
    `for non-significant risk as discussed in the <a href="https://doi.org/10.1016/j.brs.2025.10.007" target="_blank"><strong>ITRUSST paper on biophysical safety</strong></a>. ` +
    `The dots show the pressure provided in the input parameters panel without derating, ` +
    `with derating for soft tissues only (using an attenuation coefficient of 0.3 dB/cm/MHz) and with derating for soft tissues and ` +
    `bone (using the function reported by <a href="https://doi.org/10.1016/j.brs.2022.12.005" target="_blank"><strong>Attali et al. 2023</strong></a>). ` +
    `Please see the tooltips for MI for further details.`
const initOptions : EChartsInitOpts = {
  renderer: "svg",
  devicePixelRatio: 2,
}

function writeToFile(svgString) {
  const blob = new Blob([svgString], { type: 'image/svg+xml' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url;
  a.download = `plot.svg`
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function saveChartAsImage () {
  if (chartRef.value) {
    writeToFile(chartRef.value.chart
        .renderToSVGString()
        .replace(/fill="#FFF"/g, 'fill="#333"'));
  }
}

const commonTextStyle = {
  color: isDarkModeGlobal.value ? "#FFF" : "#333",
  fontFamily: "'Roboto', '-apple-system', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  fontWeight: 'normal'
}

function redraw() {
  chartRef.value.setOption({
    darkMode: 'auto',

    color: ['#5470C6', '#66EEE9FF', '#FF2424FF', '#ffaf00', '#b500f1'],
    title: {
          text: "Regulatory limits on pressure",
      left: '160',
      top: "0",
      textStyle: {
        color: isDarkModeGlobal.value ? "#FFF" : "#333",
        fontSize: 16,
        fontFamily: "'Roboto', '-apple-system', 'Helvetica Neue', Helvetica, Arial, sans-serif",
        fontWeight: 'normal',
      },
    },
    tooltip: {
      trigger: 'axis'
    },
    grid: {
      zlevel: 0,
      left: '5%',
      right: '16%',
      top: '34%',
      bottom: '20%',
    },
    legend: {
      top: 28,
      type: 'plain',
      left: '0%',
      orient: 'horizontal',
      textStyle: {
        ...commonTextStyle,
        fontSize: 11,
      },
      fontSize: 8,
      data: [
        'Pressure (MI limit)',
        'Pressure corresponding to non-derated MI                                         ',
        'Pressure corresponding to derated MI (only soft tissue attenuation accounted for)',
        'Pressure corresponding to derated MI (soft tissue and skull attenuation accounted for)']
    },
    xAxis: {
      name: 'Frequency (MHz)',
      nameLocation: 'middle',
      nameTextStyle: {
        color: commonTextStyle.color,
        padding: 8
      },
      minorTick: {
        show: true
      },
      minorSplitLine: {
        show: false
      }
    },
    yAxis: {
      name: 'Pressure (MPa)',
      nameLocation: 'middle',
      nameTextStyle: {
        color: commonTextStyle.color,
        padding: 4
      },
      min: 0,
      max: 4,
      minorTick: {
        show: true
      },
      minorSplitLine: {
        show: false
      }
    },
    series: [
      {
        name: 'Pressure (MI limit)',
        type: 'line',
        showSymbol: false,
        clip: false,
        smooth: false,
        data: calculatorStore.generatePrMIData()
      },
      {
        name: 'Pressure corresponding to non-derated MI                                         ',
        type: 'scatter',
        symbolSize: 10,
        data: calculatorStore.getMyNotDeratedValue()
      },
      {
        name: 'Pressure corresponding to derated MI (only soft tissue attenuation accounted for)',
        type: 'scatter',
        symbolSize: 10,
        data: calculatorStore.getMyPCTDMIOSTACFValue()
      },
      {
        name: 'Pressure corresponding to derated MI (soft tissue and skull attenuation accounted for)',
        type: 'scatter',
        symbolSize: 10,
        data: calculatorStore.getMyValue()
      }
    ],
    graphic: [
        {
        type: "text",
        left: "8.5%",
        top: "35%",
        style: {
          text: `MI limit for a depth of ${calculatorStore.depth_mm} mm`,
          font: "bold 15px sans-serif",
          fill: isDarkModeGlobal.value ? "#FFF" : "#333",
        },
        z: 10
      },
      {
        type: "text",
        left: "8.5%",
        top: "39%",
        style: {
          text: `Focal depth - coupling thickness = ${calculatorStore.depth_mm} mm`,
          font: "bold 15px sans-serif",
          fill: isDarkModeGlobal.value ? "#FFF" : "#333",
        },
        z: 10
      },
      ]
  })
}
watch([ppp_mpa, derated_ppp_mpa, coupling_thickness_mm, focal_depth_mm, frequency_MHz] , () => redraw())
onMounted(() => redraw())
</script>
